import React, { useRef, useState, useCallback } from 'react';
import {
  Box,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Typography,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  PushPin,
  Visibility,
  ThumbUp,
  ChatBubbleOutline,
} from '@mui/icons-material';
import { NewsItemDto } from '../../../app/services/api/news';
import { getDefaultNewsImage } from '../../../assets';
import { Section } from '../../../components/ui/Section';
import { NewsModal } from './NewsModal';

interface NewsCarouselProps {
  pinnedItems: NewsItemDto[];
  otherItems: NewsItemDto[];
  loading?: boolean;
  error?: string | null;
}

function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function NewsCard({ item, isPinned, onClick }: { item: NewsItemDto; isPinned?: boolean; onClick?: () => void }) {
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);

  const showFallback = !item.imageUrl || imageError;

  return (
    <Section
      style={{
        minWidth: isPinned ? 360 : 280,
        maxWidth: isPinned ? 360 : 280,
        height: isPinned ? 340 : 300,
        marginLeft: isPinned ? 0 : 4,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        borderColor: isPinned ? theme.palette.primary.main : theme.palette.custom.gray[300],
        borderWidth: isPinned ? 3 : 1,
        overflow: 'hidden',
        backgroundColor: isPinned ? theme.palette.primary.light + '08' : undefined,
        boxShadow: isPinned ? '0 6px 20px rgba(0,0,0,0.2)' : undefined,
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isPinned ? '0 8px 24px rgba(0,0,0,0.25)' : '0 4px 12px rgba(0,0,0,0.15)',
        },
      }}
      onClick={onClick}
    >
      {/* Header/Image area - 50% of card height */}
      <Box
        sx={{
          height: '50%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={showFallback
            ? getDefaultNewsImage(Math.abs(item.id) % 4)
            : item.imageUrl!
          }
          alt={item.title}
          onError={() => setImageError(true)}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        {/* Radial gradient overlay - transparent center, darker edges */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%)',
            pointerEvents: 'none',
          }}
        />
        {/* Pinned chip overlay on top right */}
        {isPinned && (
          <Chip
            icon={<PushPin sx={{ fontSize: 12, transform: 'rotate(45deg)' }} />}
            label="PINNED"
            size="small"
            color="primary"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              height: 20,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.5px',
              zIndex: 1,
            }}
          />
        )}
      </Box>
      <CardContent sx={{ flex: 1, p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', flexDirection: 'column' }}>
        {/* Date and Tag row - opposite sides */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={0.5}
        >
          <Typography
            variant="textSM"
            color="text.secondary"
            sx={{ fontSize: 11 }}
          >
            {formatDate(item.publishedAtUtc || item.createdAtUtc)}
          </Typography>
          {item.tags && item.tags.length > 0 && (
            <Chip
              label={item.tags[0]}
              size="small"
              sx={{ height: 16, fontSize: 10 }}
            />
          )}
        </Stack>
        <Typography
          variant="textMD"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.3,
            mb: 0.5,
          }}
        >
          {item.title}
        </Typography>
        <Typography
          variant="textSM"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            fontSize: 11,
            flex: 1,
          }}
        >
          {item.summary}
        </Typography>
        {/* Views, Likes, Comments - spaced across bottom */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mt="auto"
          pt={1}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
          >
            <Visibility sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography
              variant="textSM"
              color="text.secondary"
              sx={{ fontSize: 11 }}
            >
              {item.viewCount}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
          >
            <ThumbUp sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography
              variant="textSM"
              color="text.secondary"
              sx={{ fontSize: 11 }}
            >
              {item.likeCount}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
          >
            <ChatBubbleOutline sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography
              variant="textSM"
              color="text.secondary"
              sx={{ fontSize: 11 }}
            >
              {item.commentCount}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Section>
  );
}

export function NewsCarousel({ pinnedItems, otherItems, loading, error }: NewsCarouselProps) {
  const theme = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedItem, setSelectedItem] = useState<NewsItemDto | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Limit to 1 pinned item, rest go to carousel
  const displayedPinnedItem = pinnedItems.length > 0 ? pinnedItems[0] : null;
  const extraPinnedItems = pinnedItems.slice(1);
  const carouselItems = [...extraPinnedItems, ...otherItems];

  const handleCardClick = (item: NewsItemDto) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleItemUpdated = (updatedItem: NewsItemDto) => {
    // Update local state if needed - parent component could handle refresh
    setSelectedItem(updatedItem);
  };

  const animatedScroll = useCallback((direction: 'left' | 'right') => {
    if (scrollContainerRef.current && !isAnimating) {
      setIsAnimating(true);
      const container = scrollContainerRef.current;
      const scrollAmount = 300;
      const start = container.scrollLeft;
      const target = direction === 'left'
        ? Math.max(0, start - scrollAmount)
        : Math.min(container.scrollWidth - container.clientWidth, start + scrollAmount);
      const duration = 300;
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Easing function: easeOutCubic
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        container.scrollLeft = start + (target - start) * easeProgress;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [isAnimating]);

  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ overflowX: 'hidden' }}
        >
          {[1, 2, 3, 4].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              width={280}
              height={200}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  if (error) {
    return null; // Silently hide on error - news is not critical
  }

  const hasNews = pinnedItems.length > 0 || otherItems.length > 0;
  if (!hasNews) {
    return null;
  }

  const hasCarouselItems = carouselItems.length > 0;

  return (
    <Box sx={{ mb: 3, position: 'relative' }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ pb: 1, position: 'relative' }}
      >
        {/* Single pinned item - always visible, outside carousel */}
        {displayedPinnedItem && (
          <NewsCard
            key={`pinned-${displayedPinnedItem.id}`}
            item={displayedPinnedItem}
            isPinned
            onClick={() => handleCardClick(displayedPinnedItem)}
          />
        )}

        {/* Scrollable carousel for other items (including extra pinned items) */}
        {hasCarouselItems && (
          <Box sx={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
            {/* Left scroll button */}
            <IconButton
              size="small"
              onClick={() => animatedScroll('left')}
              disabled={isAnimating}
              sx={{
                position: 'absolute',
                left: 0,
                zIndex: 2,
                bgcolor: theme.palette.background.paper,
                boxShadow: 2,
                '&:hover': { bgcolor: theme.palette.custom.gray[100] },
                '&:disabled': { opacity: 0.5 },
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>

            <Stack
              direction="row"
              spacing={2}
              ref={scrollContainerRef}
              sx={{
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                flex: 1,
                minWidth: 0,
                mx: 5, // Add margin to make room for buttons
              }}
            >
              {carouselItems.map((item) => (
                <NewsCard
                  key={`news-${item.id}`}
                  item={item}
                  onClick={() => handleCardClick(item)}
                />
              ))}
            </Stack>

            {/* Right scroll button */}
            <IconButton
              size="small"
              onClick={() => animatedScroll('right')}
              disabled={isAnimating}
              sx={{
                position: 'absolute',
                right: 0,
                zIndex: 2,
                bgcolor: theme.palette.background.paper,
                boxShadow: 2,
                '&:hover': { bgcolor: theme.palette.custom.gray[100] },
                '&:disabled': { opacity: 0.5 },
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Stack>

      {/* News Modal */}
      <NewsModal
        open={modalOpen}
        item={selectedItem}
        onClose={handleCloseModal}
        onItemUpdated={handleItemUpdated}
      />
    </Box>
  );
}
